import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { errorText } from '../../utils/errorText';
import { can } from '../../utils/permissions';
import Turnstile from '../../components/auth/Turnstile';

/*
 * The administrator's test modes (routes/test_modes.py, migration 0107;
 * docs/PUBLIC_PORTAL_AND_ORDERING_PLAN.md 1c, decision P3). System admins
 * only (testmode:manage); anyone else reaching it by a link is told so and
 * the server is asked nothing.
 *
 * Outside production the sign-up's CAPTCHA is simulated, and here it is made
 * to pass or fail, to try a sign-up both ways. In production there is nothing
 * to simulate -- the server refuses the switch, whatever is stored -- and the
 * page offers only the live check: solve the real widget, and the server
 * verifies that one token end to end (the egress Lambda and the secret)
 * without making an account. Phase 3 adds the payments' test mode here.
 */
export default function TestModes() {
  const { t } = useTranslation('testModes');
  const { user } = useAuth();
  const allowed = can(user, 'testmode:manage');
  const [state, setState] = useState(null);
  // { text: the server's words, or null; key: this page's own line }
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [token, setToken] = useState(null);
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = useCallback(() => api.get('/admin/test-modes')
    .then(res => { setError(null); setState(res.data); })
    .catch(err => setError({ text: errorText(err), key: 'loadFailed' })), []);
  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  if (!allowed) return <p style={muted}>{t('notAllowed')}</p>;

  const setOutcome = async (outcome) => {
    // Shown at once: a controlled radio set only after the save comes back
    // keeps the old choice on screen (React's checked tracking).
    setState(s => ({ ...s, captcha: { ...s.captcha, outcome } }));
    setSaving(true);
    setError(null);
    try {
      const res = await api.put('/admin/test-modes', { captcha: outcome });
      setState(res.data);
    } catch (err) {
      setError({ text: errorText(err), key: 'saveFailed' });
      load();
    } finally {
      setSaving(false);
    }
  };

  const runCheck = async () => {
    setChecking(true);
    setCheck(null);
    try {
      const res = await api.post('/admin/test-modes/captcha-check', { token });
      setCheck(res.data);
    } catch (err) {
      setCheck({ passed: false, reason: errorText(err) || 'error' });
    } finally {
      setChecking(false);
    }
  };

  const captcha = state?.captcha;
  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ marginTop: 0 }}>{t('title')}</h2>
      <p style={muted}>{t('intro')}</p>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error.text || t(error.key)}</p>}
      {state && (
        <p data-testid="test-modes-environment" style={banner(state.production)}>
          {state.production ? t('production', { env: state.environment }) : t('notProduction', { env: state.environment })}
        </p>
      )}

      {captcha && (
        <section style={box} aria-labelledby="test-captcha-title">
          <h3 id="test-captcha-title" style={{ margin: '0 0 6px', fontSize: 16 }}>{t('captcha.title')}</h3>
          <p style={muted} data-testid="test-modes-captcha-mode">{t(`captcha.mode.${captcha.mode}`)}</p>
          {!state.production && (
            <fieldset style={{ border: 'none', padding: 0, margin: '8px 0' }}>
              <legend style={{ fontWeight: 600, marginBottom: 6 }}>{t('captcha.outcome')}</legend>
              {['pass', 'fail'].map(outcome => (
                <label key={outcome} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginInlineEnd: 16 }}>
                  <input type="radio" name="captcha-outcome" value={outcome} checked={captcha.outcome === outcome}
                    disabled={saving} onChange={() => setOutcome(outcome)} data-testid={`test-modes-${outcome}`} />
                  {t(`captcha.${outcome}`)}
                </label>
              ))}
              <p style={muted}>{t('captcha.outcomeHelp')}</p>
            </fieldset>
          )}

          <h4 style={{ margin: '12px 0 6px', fontSize: 14 }}>{t('check.title')}</h4>
          <p style={muted}>{t(state.production ? 'check.helpProduction' : 'check.help')}</p>
          <Turnstile action="check" onToken={setToken} />
          <button type="button" onClick={runCheck} disabled={checking || !token} title={t('tip.check')}
            data-testid="test-modes-check" style={btn}>
            {checking ? t('check.running') : t('check.run')}
          </button>
          {check && (
            <p role="status" data-testid="test-modes-check-result"
              style={{ color: check.passed ? 'var(--c-success-fg)' : 'var(--c-danger-fg)', marginTop: 8 }}>
              {t(check.passed ? 'check.passed' : 'check.failed', { reason: check.reason })}
            </p>
          )}
        </section>
      )}
    </div>
  );
}

const muted = { fontSize: 13, color: 'var(--c-text-muted-fg)', margin: '0 0 8px' };
const box = { background: 'var(--c-bg-subtle)', border: '1px solid var(--c-border)', borderRadius: 8, padding: 16,
  marginTop: 12 };
const banner = (production) => ({
  padding: '8px 12px', borderRadius: 6, fontSize: 13, margin: '8px 0',
  background: production ? 'var(--c-danger-soft)' : 'var(--c-success-soft)',
  color: production ? 'var(--c-danger-2-fg)' : 'var(--c-success-3-fg)',
});
const btn = { padding: '8px 14px', borderRadius: 6, border: '1px solid var(--c-primary)', background: 'var(--c-primary)',
  color: 'var(--c-on-accent)', cursor: 'pointer', fontSize: 13 };
